import { XIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useListCopy } from 'app/hooks/useListCopy'
import { closeModal, showModal } from 'app/lib/modal'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { isAssignablePipeline } from './systemPipelines'
import { PipelineCatalogEntry } from './usePipelinesCatalog'
import { NOT_STARTED_STAGE } from './systemPipelines'
import { cn } from 'app/lib/utils'
import { ContactJson } from 'app/types/entities'

const FALLBACK_STAGE_COLOR = `var(--line-strong)`

/**
 * Stage colors are theme tokens (`pipelines-silver`) backed by
 * `--color-pipelines-*` CSS variables — resolve them for inline styles, while
 * tolerating raw CSS colors and unknown tokens.
 */
function stageCssColor(color?: string): string {
  const value = color?.trim()
  if (!value) return FALLBACK_STAGE_COLOR
  if (/^(#|rgb|hsl|oklab|oklch)/.test(value)) return value
  return `var(--color-${value}, ${FALLBACK_STAGE_COLOR})`
}

/**
 * How many memberships render inside the focus card. Rows stack vertically and
 * long pipelines wrap, so an uncapped list is the one part of the card whose
 * height a contact's own data can drive off the screen. The rest move into a
 * dialog, which scrolls on its own.
 */
const INLINE_LIMIT = 2

/**
 * Pipelines this card's "Add to pipeline" menu may offer: not already joined, with at
 * least one stage to land on, and assignable for this contact. A system pipeline is
 * offered only when its board would actually draw them (see `isAssignablePipeline`);
 * the card always has the contact in hand, so it can ask.
 *
 * A pure function so the filtering rule has coverage without mounting the card.
 */
export function joinablePipelines(
  catalog: PipelineCatalogEntry[],
  memberships: NonNullable<ContactJson[`pipelines`]>,
  contact?: ContactJson,
): PipelineCatalogEntry[] {
  return catalog.filter(
    (pipeline) =>
      pipeline.stages.length > 0 &&
      !memberships.some((m) => m.id === pipeline.id) &&
      isAssignablePipeline(pipeline, contact),
  )
}

/**
 * The memberships a card shows: what the record holds, plus the pipeline the
 * surface implies when the record is silent about it.
 *
 * Saved first, implied last: what a contact actually joined outranks what the
 * screen behind them suggests. A pipeline the catalog does not know is
 * dropped rather than drawn as a nameless row.
 *
 * Pure so the rule has coverage without mounting the card.
 */
export function withImplicitMembership(
  saved: NonNullable<ContactJson[`pipelines`]>,
  implicitPipelineId: string | undefined,
  catalog: PipelineCatalogEntry[],
): NonNullable<ContactJson[`pipelines`]> {
  if (!implicitPipelineId) return saved
  if (saved.some((membership) => membership.id === implicitPipelineId)) return saved
  if (!catalog.some((pipeline) => pipeline.id === implicitPipelineId)) return saved
  return [...saved, { id: implicitPipelineId, stage: undefined } as (typeof saved)[number]]
}

type MembershipRowProps = {
  membership: NonNullable<ContactJson[`pipelines`]>[number]
  catalog: PipelineCatalogEntry[]
  /** Implied by the surface rather than saved. See `implicitPipelineId`. */
  implicit?: boolean
  savingId?: string
  onMoveStage: (pipelineId: string, stageName: string, pipelineName?: string) => void
  onRemove: (pipelineId: string, pipelineName?: string) => void
  canRemove?: (pipelineId: string) => boolean
}

/**
 * One pipeline membership as a stage ladder. Rendered both inline in the focus
 * card and inside the overflow dialog, so both stay interactive and identical.
 */
function MembershipRow({
  membership,
  catalog,
  implicit,
  savingId,
  onMoveStage,
  onRemove,
  canRemove,
}: MembershipRowProps) {
  const copy = useListCopy()
  const pipeline = catalog.find((p) => p.id === membership.id)
  const stages = pipeline?.stages ?? []
  const currentIndex = stages.findIndex((s) => s.name === membership.stage)
  const currentColor = stageCssColor(stages[currentIndex]?.color)
  const isSaving = savingId === membership.id
  // A single-stage pipeline has no journey to draw and nowhere to move to,
  // so the ladder would just be one long bar saying nothing. The label
  // already names the stage; let it take the full row instead.
  const showLadder = stages.length > 1
  const locked = pipeline?.lockedStage
  // Nobody moves into or out of a stage only a booking sets, so a contact in it has no segment to click either.
  const frozen = !!locked && membership.stage === locked
  return (
    <div className="group/rail flex w-full items-center gap-3">
      <div className={cn(`min-w-0`, showLadder ? `w-36 shrink-0` : `flex-1`)}>
        <p className="truncate text-label-sm text-strong">{pipeline?.name ?? copy.Noun}</p>
        <p className="flex items-center gap-1.5 truncate text-body-xs text-subtle">
          <span className="inline-block h-1.5 w-1.5 shrink-0" style={{ backgroundColor: currentColor }} />
          {membership.stage ?? NOT_STARTED_STAGE}
          {isSaving ? (
            <span className="text-minimal">· saving…</span>
          ) : (
            currentIndex >= 0 &&
            stages.length > 1 && (
              <span className="text-minimal">
                · {currentIndex + 1}/{stages.length}
              </span>
            )
          )}
        </p>
      </div>
      {showLadder && (
        // Segments are a FIXED width and wrap. Sizing them `flex-1` made
        // the geometry a function of the stage count, which is
        // user-defined and unbounded: three stages rendered as fat
        // lozenges, twelve as unclickable slivers. Fixed width keeps one
        // rhythm and a real hit target at any count, and long pipelines
        // wrap to a second row instead of degrading.
        <div
          className={cn(
            `flex min-w-0 flex-1 flex-wrap items-center gap-1.5 transition-opacity`,
            isSaving && `opacity-60`,
          )}
        >
          {stages.map((stage, index) => {
            const reached = currentIndex >= 0 && index <= currentIndex
            const isCurrent = index === currentIndex
            const movable = !frozen && stage.name !== locked
            const Segment = movable ? `button` : `span`
            const segment = (
              <Segment
                type={movable ? `button` : undefined}
                onClick={
                  movable ? () => !isCurrent && onMoveStage(membership.id, stage.name, pipeline?.name) : undefined
                }
                aria-label={movable ? `Move to ${stage.name}` : undefined}
                className={cn(
                  // Fixed height on purpose: a size change on hover makes
                  // the row reflow out from under the cursor, which
                  // ping-pongs enter/leave and pegs the main thread.
                  // Hover feedback is paint-only (tint + brightness).
                  // Inset ring also keeps light stage colors (e.g. silver)
                  // legible against the tile background.
                  `block h-2.5 w-9 rounded-full ring-1 ring-inset ring-default transition-colors duration-150`,
                  // Every pill wears its own stage color. Stages still
                  // ahead show it ghosted rather than a flat gray, so the
                  // rail reads as this pipeline's palette while solid vs
                  // faded still tells you how far along the contact is.
                  reached
                    ? `bg-[var(--stage)]`
                    : cn(
                        `bg-[color-mix(in_srgb,var(--stage)_22%,transparent)]`,
                        movable && `hover:bg-[color-mix(in_srgb,var(--stage)_45%,transparent)]`,
                      ),
                  isCurrent && `shadow-elevation-1 ring-2 ring-strong`,
                  !isCurrent && movable && `cursor-pointer hover:brightness-95`,
                )}
                style={{ '--stage': stageCssColor(stage.color) } as React.CSSProperties}
              />
            )
            return (
              <TooltipWrapper
                key={stage.name}
                className="shrink-0"
                message={
                  isCurrent
                    ? `${stage.name} (current)`
                    : movable
                      ? `Move to ${stage.name}`
                      : stage.name === locked
                        ? `${stage.name} (set by a booking)`
                        : stage.name
                }
              >
                {segment}
              </TooltipWrapper>
            )
          })}
        </div>
      )}
      {/* Nothing to remove on a row nobody joined. Offering it would write the
          same array back and report a removal that never happened. A row the
          caller will not remove (a filter list's) gets no control at all. */}
      {!implicit && (canRemove?.(membership.id) ?? true) && (
        <TooltipWrapper message={copy.removeFromList}>
          <button
            type="button"
            onClick={() => onRemove(membership.id, pipeline?.name)}
            aria-label={copy.removeFromList}
            className="shrink-0 rounded p-1 text-minimal opacity-0 transition group-hover/rail:opacity-100 hover:bg-red-50 hover:text-red-600"
          >
            <XIcon size={14} weight="bold" />
          </button>
        </TooltipWrapper>
      )}
    </div>
  )
}

/**
 * The kanban, compressed into the card: each pipeline membership renders as a
 * stage ladder — stages behind the contact filled in their kanban colors, the
 * road ahead ghosted in the same palette. Hover names a stage, click moves
 * there. Past `INLINE_LIMIT` memberships the rest move into a dialog so the
 * card's height stays bounded no matter how many pipelines a contact joins.
 */
export function PipelineRail({
  contact,
  catalog,
  implicitPipelineId,
  savingId,
  onMoveStage,
  onRemove,
  onJoin,
  canRemove,
  canJoin,
}: {
  contact: ContactJson
  catalog: PipelineCatalogEntry[]
  /**
   * A pipeline this contact belongs to by virtue of the surface they were
   * opened from, rather than by a saved membership.
   *
   * A board's first column holds everyone its queue found who has no
   * membership yet, so a card opened out of it is on a pipeline as far as the
   * screen behind it is concerned, while the record says nothing. Without
   * this the card answered "Hygiene Recall?" with "add to pipeline", which
   * contradicts the board it was opened from. Shown at no stage, with the
   * ladder live: clicking a stage is what writes the membership.
   */
  implicitPipelineId?: string
  /** Pipeline id with a save in flight. */
  savingId?: string
  onMoveStage: (pipelineId: string, stageName: string, pipelineName?: string) => void
  onRemove: (pipelineId: string, pipelineName?: string) => void
  onJoin: (pipelineId: string, stageName: string, pipelineName?: string) => void
  /** Whether a membership row offers Remove. Every row does when omitted. */
  canRemove?: (pipelineId: string) => boolean
  /** Whether the Add menu may offer a pipeline, on top of `joinablePipelines`. Every one may when omitted. */
  canJoin?: (pipelineId: string) => boolean
}) {
  const copy = useListCopy()
  const saved = contact.pipelines ?? []
  const memberships = withImplicitMembership(saved, implicitPipelineId, catalog)
  // Which row, if any, is the implied one: it is the only member of the list
  // the record does not account for.
  const isImplicit = (id: string) => memberships.length > saved.length && id === implicitPipelineId
  const joinable = joinablePipelines(catalog, memberships, contact).filter((pipeline) => canJoin?.(pipeline.id) ?? true)
  const inline = memberships.slice(0, INLINE_LIMIT)
  const overflow = memberships.length - inline.length

  const rowProps = { catalog, savingId, onMoveStage, onRemove, canRemove }

  return (
    <div className="flex w-full flex-col gap-2.5">
      {inline.map((membership) => (
        <MembershipRow key={membership.id} membership={membership} implicit={isImplicit(membership.id)} {...rowProps} />
      ))}

      {overflow > 0 && (
        <button
          type="button"
          onClick={() =>
            showModal(
              // Caps at the viewport and scrolls, so any number of
              // memberships fits without running off the screen.
              <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto text-left">
                {memberships.map((membership) => (
                  <MembershipRow
                    key={membership.id}
                    membership={membership}
                    implicit={isImplicit(membership.id)}
                    {...rowProps}
                  />
                ))}
              </div>,
              {
                useRadix: true,
                isFullScreen: false,
                title: copy.NounPlural,
                panelClassName: `min-w-0 sm:max-w-xl`,
                onClose: closeModal,
              },
            )
          }
          className="w-fit text-body-xs text-primary-600 transition hover:underline"
        >
          Show {overflow} more {overflow === 1 ? copy.noun : copy.nounPlural}
        </button>
      )}

      {joinable.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-7 w-fit shrink-0 items-center gap-1 rounded-md border border-dashed border-strong px-2 text-body-sm text-subtle transition-colors hover:border-(--fg-minimal) hover:text-default"
            >
              + {memberships.length ? `Add` : copy.addToList}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel className="text-body-xs font-normal uppercase tracking-wide text-subtle">
              {copy.addToList}
            </DropdownMenuLabel>
            {joinable.map((pipeline) => (
              <DropdownMenuItem
                key={pipeline.id}
                onClick={() => onJoin(pipeline.id, pipeline.stages[0].name, pipeline.name)}
              >
                {pipeline.name}
                <span className="ml-2 text-body-xs text-minimal">→ {pipeline.stages[0].name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
